import * as React from 'react';
import { IntlShape } from 'react-intl';
import GoogleMapsService from '../../Services/GoogleMapsService';
import PositionTooltip from './PositionTooltip';
import {
  IRideMapMarkers,
  IRideMapMarkerTooltips,
  TTooltip,
  TToggleTooltip,
  IRide,
  IAnnouncement,
  IRidePosition,
  IRideDetail,
} from './types';
import {
  ALARM_ACTIVE_LINE_COLOR,
  IS_ALARM,
  IS_START_POSITION,
  IS_STOP_POSITION,
} from './constants';
import { getAlarmsFromAnnouncementsInPosition } from './RidePlayerService';
import { positionMarkerIcon } from './RidePlayerIconService';

export interface IRideMapAlarmPositionMarkerTooltips {
  [index: number]: google.maps.OverlayView[];
}

export interface IRideMapAlarmMarkers {
  [rideNumber: number]: (google.maps.Marker | null)[];
}

export interface IRideMapCombinedAlarmMarkers {
  [rideNumber: number]: {
    [positionIndex: number]: {
      ids: number[];
      marker: google.maps.Marker;
    };
  };
}

export interface IRideMapAlarmPositionMarkers {
  [rideNumber: number]: google.maps.Marker[];
}

/**
 * Create alarm marker
 * @param {google.maps.Map} map
 * @param {google.maps.LatLng} position
 * @returns {google.maps.Marker}
 */
export const createAlarmIconMarker = (
  map: google.maps.Map | undefined,
  position: google.maps.LatLng,
  infoWindow?: TTooltip
): google.maps.Marker => {
  const icon: google.maps.Icon = {
    url: '/assets/images/player/AlarmPositionMarker.svg',
    anchor: new google.maps.Point(16, 16),
  };
  const markerOptions: google.maps.MarkerOptions = {
    icon,
    position,
    map,
    zIndex: 99,
  };
  const marker = new google.maps.Marker(markerOptions);
  if (infoWindow) {
    marker.addListener('mouseover', () =>
      infoWindow.toggleTooltipCallback(infoWindow.rideNumber, infoWindow.positionIndex)
    );
    marker.addListener('mouseout', () =>
      infoWindow.toggleTooltipCallback(infoWindow.rideNumber, infoWindow.positionIndex)
    );
  }
  return marker;
};

/**
 * Create combined alarm icon markers
 * @param {google.maps.Map|undefined} map
 * @param {IRide[]} rides
 * @param {TToggleTooltip} handleTooltip
 * @returns
 */
export const createCombinedAlarmIconMarkers = (
  map: google.maps.Map | undefined,
  rides: IRide[],
  handleTooltip: TToggleTooltip,
  activeRide: number
): IRideMapCombinedAlarmMarkers => {
  const alarmMarkers: IRideMapCombinedAlarmMarkers = {};
  for (let rideIndex = 0; rideIndex < rides.length; rideIndex += 1) {
    const rideNumber = rideIndex + 1;
    alarmMarkers[rideNumber] = {};
    const ride = rides[rideIndex];
    for (let positionIndex = 0; positionIndex < ride.positions.length; positionIndex += 1) {
      const prevPosition = ride.positions[positionIndex - 1];
      const currentPosition = ride.positions[positionIndex];
      let prevPositionAlarms: IAnnouncement[] = [];

      // if (!prevPosition && currentPosition.alarms.length === 0) {
      //   console.log(
      //     '1. prevposition NOT exists and current position has NO alarms: ',
      //     positionIndex
      //   );
      // }
      const currentPositionAlarms = getAlarmsFromAnnouncementsInPosition(currentPosition);
      if (prevPosition) {
        prevPositionAlarms = getAlarmsFromAnnouncementsInPosition(prevPosition);
        prevPositionAlarms = prevPosition.announcements.filter(
          (announcement: IAnnouncement) => announcement.type === IS_ALARM
        );
      }
      // If previous position not exists and current position has alarms, create alarm marker
      if (!prevPosition && currentPositionAlarms.length > 0) {
        // console.log('2. prevposition NOT exists and current position HAS alarms: ', positionIndex);
        const alarmMarker = createAlarmIconMarker(
          rideNumber === activeRide ? map : undefined,
          new google.maps.LatLng(
            ride.positions[positionIndex].gpsLat,
            ride.positions[positionIndex].gpsLon
          ),
          { rideNumber, positionIndex, toggleTooltipCallback: handleTooltip }
        );
        let ids: number[] = [];
        // ids = ride.positions[positionIndex].alarms.map((alarm: IVehicleRideAlarm) => alarm.id);
        ids = currentPositionAlarms.map((alarm: IAnnouncement) => alarm.id);
        alarmMarkers[rideNumber][positionIndex] = { ids, marker: alarmMarker };
      }
      // if (prevPosition && prevPosition.alarms.length === 0 && currentPosition.alarms.length === 0) {
      //   console.log('3. prevPosition exists and has NO alarms and current position has no alarms: ', positionIndex);
      // }

      // If previous position exists and has no alarms and current position has alarms
      if (prevPosition && prevPositionAlarms.length === 0 && currentPositionAlarms.length > 0) {
        // console.log('4. prevPosition exists and has NO alarms and current position HAS alarms: ', positionIndex);
        const alarmMarker = createAlarmIconMarker(
          rideNumber === activeRide ? map : undefined,
          new google.maps.LatLng(
            ride.positions[positionIndex].gpsLat,
            ride.positions[positionIndex].gpsLon
          ),
          { rideNumber, positionIndex, toggleTooltipCallback: handleTooltip }
        );
        let ids: number[] = [];
        // ids = ride.positions[positionIndex].alarms.map((alarm: IVehicleRideAlarm) => alarm.id);
        ids = currentPositionAlarms.map((alarm: IAnnouncement) => alarm.id);
        alarmMarkers[rideNumber][positionIndex] = { ids, marker: alarmMarker };
      }
      // if (prevPosition && prevPosition.alarms.length > 0 && currentPosition.alarms.length === 0) {
      //   console.log('5. prevPosition exists and has alarms and current position has NO alarms: ', positionIndex);
      // }

      // If previous postion exist and has alarms and curren position alarms
      if (prevPosition && prevPositionAlarms.length > 0 && currentPositionAlarms.length > 0) {
        // console.log('6. prevPosition exists and has alarms and current position has alarms: ', prevPosition.alarms, currentPosition.alarms, positionIndex, );

        // if (alarmMarkers[rideNumber][positionIndex - 1]) {
        //   // console.log('6.1 on prev position alarm marker exist', alarmMarkers[rideNumber][positionIndex - 1]);
        //   const prevAlarmIds = alarmMarkers[rideNumber][positionIndex - 1].ids;
        //   const currentPositionAlarmIds = currentPosition.alarms.map(
        //     (alarm: IVehicleRideAlarm) => alarm.id
        //   );
        //   // console.log('6.1.0, prevAlarms / currAlarms: ', prevAlarmIds, currentPositionAlarmIds, positionIndex);
        //   // if (JSON.stringify(prevAlarmIds) === JSON.stringify(currentPositionAlarmIds)) {
        //   //   console.log('6.1.1 prev position alarms are EQUAL with current position alarms', positionIndex);
        //   // } else {
        //   //   console.log('6.1.2 prev position alarms are NOT EQUAL with current position alarms', positionIndex);
        //   // }
        // }

        // If alarm marker not exist for prev position
        if (!alarmMarkers[rideNumber][positionIndex - 1]) {
          // console.log('6.2.0 on prev position alarm marker not exist, but position has it', prevPosition.alarms, positionIndex);
          const prevAlarmIds = prevPositionAlarms.map((alarm: IAnnouncement) => alarm.id);
          const currentPositionAlarmIds = currentPositionAlarms.map(
            (alarm: IAnnouncement) => alarm.id
          );
          if (JSON.stringify(prevAlarmIds) === JSON.stringify(currentPositionAlarmIds)) {
            // console.log(
            //   '6.2.1 prev position alarms are EQUAL with current position alarms',
            //   positionIndex
            // );
          } else {
            // console.log(
            //   '6.2.2 prev position alarms are NOT EQUAL with current position alarms',
            //   positionIndex
            // );
            const currentPositionAlarmIdsWihtoutPreviousAlarmIds = currentPositionAlarmIds.filter(
              (id: number) => !prevAlarmIds.includes(id)
            );
            // console.log(
            //   '6.2.3 current position alarms without prev position alarms: ',
            //   currentPositionAlarmIdsWihtoutPreviousAlarmIds,
            //   positionIndex
            // );
            if (currentPositionAlarmIdsWihtoutPreviousAlarmIds.length >= 0) {
              const alarmMarker = createAlarmIconMarker(
                rideNumber === activeRide ? map : undefined,
                new google.maps.LatLng(
                  ride.positions[positionIndex].gpsLat,
                  ride.positions[positionIndex].gpsLon
                ),
                { rideNumber, positionIndex, toggleTooltipCallback: handleTooltip }
              );
              let ids: number[] = [];
              ids = currentPositionAlarms.map((alarm: IAnnouncement) => alarm.id);
              alarmMarkers[rideNumber][positionIndex] = { ids, marker: alarmMarker };
            }
          }
        }
      }
    }
  }
  return alarmMarkers;
};

/**
 * Update alarm icon markers with new position
 * @param {google.maps.Map|undefined} map
 * @param {IRideMapCombinedAlarmMarkers} alarmMarkers
 * @param {IRide} ride
 * @param {number} rideNumber
 * @param {IRidePosition} newPosition
 * @param {number} positionIndex
 * @param {TToggleTooltip} handleTooltip
 * @returns {void}
 */
export const updateAlarmIconMarkersWithNewPosition = (
  map: google.maps.Map | undefined,
  alarmMarkers: IRideMapCombinedAlarmMarkers,
  ride: IRide,
  rideNumber: number,
  newPosition: IRidePosition,
  positionIndex: number,
  handleTooltip: TToggleTooltip
): void => {
  const prevPosition = ride.positions[positionIndex - 1];
  const currentPosition = newPosition;
  const alarmsForPosition = getAlarmsFromAnnouncementsInPosition(currentPosition);
  const alarmsForPrevPosition = getAlarmsFromAnnouncementsInPosition(prevPosition);

  // if no occurence of alarm exists, create marker
  if (
    !prevPosition &&
    !alarmMarkers[rideNumber][positionIndex] &&
    currentPosition &&
    alarmsForPosition &&
    alarmsForPosition.length > 0
  ) {
    const alarmMarker = createAlarmIconMarker(
      map,
      new google.maps.LatLng(
        ride.positions[positionIndex].gpsLat,
        ride.positions[positionIndex].gpsLon
      ),
      { rideNumber, positionIndex, toggleTooltipCallback: handleTooltip }
    );

    const ids: number[] = [];
    alarmsForPosition.forEach((alarm: IAnnouncement) => {
      if (!ids.includes(alarm.id)) {
        ids.push(alarm.id);
      }
    });
    alarmMarkers[rideNumber][positionIndex] = { ids, marker: alarmMarker };
  }

  // If alarm on previous position exists and is differrent, create marker
  if (
    prevPosition &&
    !alarmMarkers[rideNumber][positionIndex] &&
    alarmMarkers[rideNumber][positionIndex - 1] &&
    currentPosition &&
    alarmsForPosition &&
    alarmsForPosition.length > 0
  ) {
    const ids: number[] = [];
    alarmsForPosition.forEach((alarm: IAnnouncement) => {
      if (!ids.includes(alarm.id)) {
        ids.push(alarm.id);
      }
    });
    if (JSON.stringify(ids) !== JSON.stringify(alarmMarkers[rideNumber][positionIndex - 1].ids)) {
      const alarmMarker = createAlarmIconMarker(
        map,
        // new google.maps.LatLng(
        //   ride.positions[positionIndex].gpsLat,
        //   ride.positions[positionIndex].gpsLon
        // ),
        new google.maps.LatLng(newPosition.gpsLat, newPosition.gpsLon),
        { rideNumber, positionIndex, toggleTooltipCallback: handleTooltip }
      );

      alarmMarkers[rideNumber][positionIndex] = { ids, marker: alarmMarker };
    }
  }

  // If alarm on previous position not exists and on current position exists - create marker
  if (
    prevPosition &&
    alarmsForPrevPosition &&
    alarmsForPrevPosition.length === 0 &&
    !alarmMarkers[rideNumber][positionIndex] &&
    !alarmMarkers[rideNumber][positionIndex - 1] &&
    currentPosition &&
    alarmsForPosition &&
    alarmsForPosition.length > 0
  ) {
    const ids: number[] = [];
    alarmsForPosition.forEach((alarm: IAnnouncement) => {
      if (!ids.includes(alarm.id)) {
        ids.push(alarm.id);
      }
    });
    const alarmMarker = createAlarmIconMarker(
      map,
      new google.maps.LatLng(currentPosition.gpsLat, currentPosition.gpsLon),
      { rideNumber, positionIndex, toggleTooltipCallback: handleTooltip }
    );

    alarmMarkers[rideNumber][positionIndex] = { ids, marker: alarmMarker };
  }
};

/**
 * Create alarm position marker
 * @param {google.maps.Map} map
 * @param {google.maps.LatLng} position
 * @returns {google.maps.Marker}
 */
export const createAlarmPositionMarker = (
  map: google.maps.Map | undefined,
  position: google.maps.LatLng,
  infoWindow?: TTooltip
): google.maps.Marker => {
  const icon: google.maps.Icon = {
    url: positionMarkerIcon(ALARM_ACTIVE_LINE_COLOR),
    anchor: new google.maps.Point(6, 6),
  };
  const markerOptions: google.maps.MarkerOptions = {
    icon,
    position,
    map,
    zIndex: 99,
  };
  const marker = new google.maps.Marker(markerOptions);
  marker.set('originalColor', ALARM_ACTIVE_LINE_COLOR);
  if (infoWindow) {
    marker.addListener('mouseover', () =>
      infoWindow.toggleTooltipCallback(infoWindow.rideNumber, infoWindow.positionIndex)
    );
    marker.addListener('mouseout', () =>
      infoWindow.toggleTooltipCallback(infoWindow.rideNumber, infoWindow.positionIndex)
    );
  }
  return marker;
};

/**
 * Create alarm position markers
 * @param {google.maps.Map} map
 * @param {IRide[]} rides
 * @param {TToggleTooltip} handleToggleInfoWindow
 * @returns {IRideMapAlarmMarkers}
 */
export const createAlarmPositionMarkers = (
  map: google.maps.Map | undefined,
  rides: IRide[],
  handleToggleInfoWindow: TToggleTooltip
): IRideMapAlarmPositionMarkers => {
  const alarmPositionMarkers: IRideMapAlarmPositionMarkers = {};
  for (let rideIndex = 0; rideIndex < rides.length; rideIndex += 1) {
    const rideNumber = rideIndex + 1;
    alarmPositionMarkers[rideNumber] = [];
    const ride = rides[rideIndex];
    for (let positionIndex = 0; positionIndex < ride.positions.length; positionIndex += 1) {
      if (
        ride.positions[positionIndex].type !== IS_START_POSITION ||
        ride.positions[positionIndex].type !== IS_STOP_POSITION
      ) {
        const alarmPositionMarker = createAlarmPositionMarker(
          map,
          new google.maps.LatLng(
            ride.positions[positionIndex].gpsLat,
            ride.positions[positionIndex].gpsLon
          ),
          { rideNumber, positionIndex, toggleTooltipCallback: handleToggleInfoWindow }
        );
        alarmPositionMarkers[rideNumber].push(alarmPositionMarker);
      }
    }
  }
  return alarmPositionMarkers;
};

/**
 * Create alarm position marker tooltips
 * @param {(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void} handleCloseInfoWindow
 * @returns {IRideMapMarkerInfoWindows}
 */
export const createAlarmPositionMarkerTooltips = (
  rides: IRide[],
  intl: IntlShape
): IRideMapAlarmPositionMarkerTooltips => {
  const positionTooltips: IRideMapAlarmPositionMarkerTooltips = {};
  for (let rideIndex = 0; rideIndex < rides.length; rideIndex += 1) {
    const rideNumber = rideIndex + 1;
    positionTooltips[rideNumber] = [];
    const ride = rides[rideIndex];
    for (let positionIndex = 0; positionIndex < ride.positions.length; positionIndex += 1) {
      const positionTooltip = (
        <PositionTooltip
          intl={intl}
          position={ride.positions[positionIndex]}
          positionIndex={positionIndex}
        />
      );
      const positionTooltipOverlay = GoogleMapsService.createAlarmPositionMarkerOverlay(
        ride.positions[positionIndex],
        positionTooltip
      );

      positionTooltips[rideNumber].push(positionTooltipOverlay);
    }
  }
  return positionTooltips;
};

/**
 * Update alarm position markers with tooltip when new position is received
 * @param {google.maps.Map} map
 * @param {IRideMapMarkers} positionMarkers
 * @param {IRideMapMarkerTooltips} positionMarkerTooltips
 * @param {number} rideNumber
 * @param {IRideDetail} rideData
 * @param {IRidePosition} newPosition
 * @param {boolean} speedlimitsEnabled
 * @param {IRidePlayerSpeedLimitsSettings} speedlimitSettings
 * @param {number} positionIndex
 * @param {IntlShape} intl
 * @param {TToggleTooltip} handleToggleTooltip
 * @returns {void}
 */
export const updateAlarmPositionMarkersWithNewPosition = (
  map: google.maps.Map | undefined,
  positionMarkers: IRideMapMarkers,
  positionMarkerTooltips: IRideMapMarkerTooltips,
  rideNumber: number,
  rideData: IRideDetail,
  newPosition: IRidePosition,
  positionIndex: number,
  intl: IntlShape,
  handleToggleTooltip?: TToggleTooltip
): void => {
  const position = new google.maps.LatLng(newPosition.gpsLat, newPosition.gpsLon);
  const marker = createAlarmPositionMarker(
    map,
    position,
    handleToggleTooltip
      ? {
          rideNumber,
          positionIndex,
          toggleTooltipCallback: handleToggleTooltip,
        }
      : undefined
  );
  if (positionMarkers[rideNumber]) {
    positionMarkers[rideNumber].push(marker);
  }
  let positionTooltip: JSX.Element | null = null;
  positionTooltip = (
    <PositionTooltip intl={intl} position={newPosition} positionIndex={positionIndex} />
  );

  const positionMarkerTooltip = GoogleMapsService.createAlarmPositionMarkerOverlay(
    newPosition,
    positionTooltip
  );

  positionMarkerTooltips[rideNumber][positionIndex] = positionMarkerTooltip;
};
